import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Modal,
  Tooltip,
  Typography,
} from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import ShipeeziNewGrid, { Column, RowActionsExtra } from "Components/AppGrid/shipeezi-new-grid";
import { useAppState } from "Context/StateContext";
import _ from "lodash";
import { useTranslation } from "react-i18next";
import { FiEdit2, FiPlusCircle, FiX } from "react-icons/fi";
import { MdOutlineCancel } from "react-icons/md";

import { Button } from "../../../../../../components/Form";
import { Color } from "../../../../../../components/Form/Button";
import ActionButtons from "../../../../../../components/Generic/ActionButtons";
import BadgeStatus from "../../../../../../components/Generic/BadgeStatus";
import { renderMilestoneDateByTypeAndReference } from "../../../../../../components/Generic/MilestoneDates";
import { ShipmentMaintenance } from "../../../Waybills/Maintenance";
import {
  ShipmentProvider,
  shouldSyncShipmentReferenceWithOrder,
} from "../../../Waybills/Maintenance/context/useContext";
import { ContainerSize } from "../../../../../../models/container.size";
import {
  MilestoneReference,
  MilestoneType,
  MilestoneValue,
} from "../../../../../../models/milestone";
import { ShipmentTracking } from "../../../../../../models/shipment.tracking";
import { Order } from "../../../../../../models/order";
import { ShipmentContainerInfo } from "../../../../../../models/shipments";
import { Waybill } from "../../../../../../models/waybill";
import WaybillsService from "../../../../../../services/waybill.service";
import { AutomaticSequenceOptions } from "../../../../../../util/enums";
import { shipmentStatus } from "../../../../../../util/shipmentStatus";
import BadgeShipmentTrackingStatus from "../../../../ShipmentTracking/components/BadgeShipmentStatus";
import ModalAddWaybill, { ModalAddWaybillHandle } from "./ModalAddWaybill";
import {
  ButtonLabel,
  ButtonsWrapper,
  EmptyContainer,
  EmptyTitle,
  Header,
  HeaderTitle,
  ShipmentTypeStyles,
  Wrapper,
} from "./styled";

const EMPTY_DOCUMENT = require("../../../../../../assets/img/empty-documents.png");

const fullScreenModalSx = {
  position: "absolute" as const,
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  bgcolor: "background.paper",
  display: "flex",
  flexDirection: "column" as const,
  outline: "none",
};

interface WaybillTabProps {
  waybills: Waybill[];
  order?: Order;
  isOnViewMode?: boolean;
  isUpdating?: boolean;
  containerSizes?: ContainerSize[];
  onCancel?: () => void;
  onSave?: () => void;
  onSaveAndPublish?: () => void;
  inlineDraftCreation?: boolean;
  showActionButtons?: boolean;
  compact?: boolean;
  onUpdate?: (waybill: Waybill[]) => void;
  onRemoveWithAction?: (shipmentId: number, companyId: number | undefined, action: "cancel" | "revert_to_draft") => void;
}

export default function WaybillTab(props: WaybillTabProps) {
  const {
    waybills,
    order,
    isOnViewMode,
    isUpdating,
    onCancel,
    onSave,
    onSaveAndPublish,
    inlineDraftCreation = false,
    showActionButtons = true,
    compact = false,
    onUpdate,
    onRemoveWithAction,
  } = props;
  const { t: translate } = useTranslation();
  const modalAddWaybillRef = useRef<ModalAddWaybillHandle>(null);
  const preModalUnsavedRef = useRef(false);
  const { hasUnsavedChanges, setHasUnsavedChanges } = useAppState();

  const [openShipmentModal, setOpenShipmentModal] = useState(false);
  const [providerKey, setProviderKey] = useState(0);
  const [draftBeingEdited, setDraftBeingEdited] = useState<Waybill | null>(null);
  const [minContainerSequence, setMinContainerSequence] = useState<number | undefined>(undefined);
  const queryClient = useQueryClient();

  const [selectedWaybillIds, setSelectedWaybillIds] = useState<number[]>([]);
  const [containerSizes, setContainerSizes] = useState<ContainerSize[]>([]);

  const [confirmRemoveTarget, setConfirmRemoveTarget] = useState<Waybill | null>(null);
  const syncedOrderReferenceRef = useRef<string | undefined>(order?.reference);

  const inlineMinContainerSequence = useMemo(() => {
    const nums = (waybills ?? [])
      .filter((w) => !!(w as Waybill & { __localDraft?: boolean }).__localDraft)
      .map((w) => parseInt((w.containerId ?? "").replace(/\D/g, ""), 10))
      .filter((n) => !isNaN(n) && n > 0);

    if (!nums.length) return undefined;
    return Math.max(...nums);
  }, [waybills]);

  useQuery(
    ["container.sizes.list"],
    () =>
      WaybillsService.getContainerSizes({
        sortBy: JSON.stringify([["label", "ASC"]]),
      }),
    {
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) setContainerSizes(response.data);
      },
    }
  );

  const openNewShipmentModal = useCallback(() => {
    preModalUnsavedRef.current = hasUnsavedChanges;
    setDraftBeingEdited(null);

    const localDrafts = (waybills ?? []).filter(
      (w) => !!(w as Waybill & { __localDraft?: boolean }).__localDraft
    );
    if (localDrafts.length > 0) {
      const nums = localDrafts
        .map((w) => parseInt((w.containerId ?? "").replace(/\D/g, ""), 10))
        .filter((n) => !isNaN(n) && n > 0);
      const maxNum = nums.length > 0 ? Math.max(...nums) : -1;
      setMinContainerSequence(maxNum >= 0 ? maxNum : undefined);
      queryClient.invalidateQueries([
        "automatic.sequence.available",
        AutomaticSequenceOptions.SHIPMENT,
      ]);
    } else {
      setMinContainerSequence(undefined);
    }

    setProviderKey((k) => k + 1);
    setOpenShipmentModal(true);
  }, [hasUnsavedChanges, waybills, queryClient]);

  const openEditShipmentModal = useCallback((row: Waybill) => {
    preModalUnsavedRef.current = hasUnsavedChanges;
    setDraftBeingEdited(row);
    setProviderKey((k) => k + 1);
    setOpenShipmentModal(true);
  }, [hasUnsavedChanges]);

  const closeShipmentModal = useCallback(() => {
    if (hasUnsavedChanges) {
      const leave = window.confirm(translate("youHaveUnsavedChangesAreYouSureYouWantToLeave"));
      if (!leave) return;
    }
    setHasUnsavedChanges(preModalUnsavedRef.current);
    setOpenShipmentModal(false);
    setDraftBeingEdited(null);
  }, [hasUnsavedChanges, setHasUnsavedChanges, translate]);

  const initialShipmentForProvider = useMemo(() => {
    if (!draftBeingEdited) return undefined;
    const { __localDraft: _ld, ...rest } = draftBeingEdited as Waybill & { __localDraft?: boolean };
    return rest as Waybill;
  }, [draftBeingEdited]);

  useEffect(() => {
    if (!inlineDraftCreation || !order?.reference) return;

    const previousSyncedReference = syncedOrderReferenceRef.current;
    let hasChanged = false;

    const nextWaybills = (waybills ?? []).map((waybill) => {
      const isLocalDraft = !!(waybill as Waybill & { __localDraft?: boolean }).__localDraft;

      if (
        !isLocalDraft ||
        !shouldSyncShipmentReferenceWithOrder(waybill.containerId, previousSyncedReference)
      ) {
        return waybill;
      }

      if (waybill.containerId === order.reference) return waybill;

      hasChanged = true;

      return {
        ...waybill,
        containerId: order.reference,
      };
    });

    syncedOrderReferenceRef.current = order.reference;

    if (hasChanged) onUpdate?.(nextWaybills);
  }, [inlineDraftCreation, onUpdate, order?.reference, waybills]);

  const handleOrderDraftConfirm = useCallback(
    (wb: Waybill) => {
      const draft = {
        ...wb,
        __localDraft: true,
        id: draftBeingEdited?.id != null ? draftBeingEdited.id : -Date.now(),
      } as Waybill & { __localDraft?: boolean };

      const existing = waybills ?? [];
      if (draftBeingEdited?.id != null) {
        onUpdate?.(existing.map((w) => (w.id === draftBeingEdited.id ? draft : w)));
      } else {
        onUpdate?.([...existing, draft]);
      }
      setHasUnsavedChanges(false);
      setProviderKey((k) => k + 1);
      setOpenShipmentModal(false);
      setDraftBeingEdited(null);
    },
    [draftBeingEdited, onUpdate, setHasUnsavedChanges, waybills]
  );

  const removeWaybillFromList = useCallback(
    (id: number) => {
      const waybillClone = _.cloneDeep(waybills);
      const indexOfWaybill = waybillClone.findIndex((waybill) => waybill.id === id);
      if (indexOfWaybill !== -1) waybillClone.splice(indexOfWaybill, 1);
      onUpdate?.(waybillClone);
    },
    [waybills, onUpdate]
  );

  const onDeleteWaybillFromOrder = useCallback(
    async (id: number, row: Waybill) => {
      const isLocalDraft = !!(row as Waybill & { __localDraft?: boolean }).__localDraft;

      if (isLocalDraft) {
        removeWaybillFromList(id);
        return;
      }

      try {
        const res = await WaybillsService.getById(id, {
          include: JSON.stringify([{ association: "jobs", attributes: ["id"] }]),
          attributes: ["id"],
        });
        const jobs = res?.data?.jobs ?? [];
        const otherOrders = jobs.filter((job) => job.id !== order?.id);

        if (otherOrders.length > 0) {
          removeWaybillFromList(id);
          return;
        }
      } catch (err) {
        console.error("Failed to fetch shipment orders:", err);
      }

      setConfirmRemoveTarget(row);
    },
    [removeWaybillFromList, order?.id]
  );

  const handleConfirmRemoveAction = useCallback(
    (action: "cancel" | "revert_to_draft") => {
      if (!confirmRemoveTarget?.id) return;
      onRemoveWithAction?.(confirmRemoveTarget.id, confirmRemoveTarget.companyId, action);
      removeWaybillFromList(confirmRemoveTarget.id);
      setConfirmRemoveTarget(null);
    },
    [confirmRemoveTarget, onRemoveWithAction, removeWaybillFromList]
  );

  const columns: Column[] = [
    { name: "containerId", title: `${translate("shipment")} #`, searchIdentifier: "container_id" },
    {
      name: "shipmentType",
      title: translate("type"),
      formatText: (text: string) => <ShipmentTypeStyles type={text}>{text}</ShipmentTypeStyles>,
      hideSearch: true,
    },
    {
      name: "containerNumbers",
      title: `${translate("containerNumber")} #`,
      searchIdentifier: "container_numbers",
    },
    {
      name: "containers",
      title: translate("containerSizeType"),
      hideSearch: true,
      hideSort: true,
      formatComponent: (containers: ShipmentContainerInfo[], row: Waybill) => {
        if (containers.length) {
          return containerSizes?.find((size) => size.value === containers[0].size)?.label;
        }

        if (row.containerSize) return row.containerSize;

        return "";
      },
    },
    {
      name: "shipmentTracking",
      title: translate("origin"),
      formatComponent: (shipmentTracking: ShipmentTracking) => shipmentTracking?.originPort ?? null,
      hideSearch: true,
      hideSort: true,
    },
    {
      name: "shipmentTracking",
      title: translate("destination"),
      formatComponent: (shipmentTracking: ShipmentTracking) => shipmentTracking?.finalPort ?? null,
      hideSearch: true,
      hideSort: true,
    },
    {
      name: "milestones",
      title: translate("etd"),
      formatComponent: (milestoneValues: MilestoneValue[]) =>
        renderMilestoneDateByTypeAndReference(
          milestoneValues,
          MilestoneType.ESTIMATED,
          MilestoneReference.WAYBILL.ESTIMATED.TIME_DEPARTURE
        ),
      hideSearch: true,
      hideSort: true,
    },
    {
      name: "milestones",
      title: translate("etaAtDestination"),
      formatComponent: (milestoneValues: MilestoneValue[]) =>
        renderMilestoneDateByTypeAndReference(
          milestoneValues,
          MilestoneType.ESTIMATED,
          MilestoneReference.WAYBILL.ESTIMATED.TIME_ARRIVAL
        ),
      hideSearch: true,
      hideSort: true,
    },
    {
      name: "statusShipment",
      title: translate("status"),
      formatComponent: (status: string, row: Waybill) => {
        if (row.marineTrafficId || row.shipmentTracking)
          return (
            <BadgeShipmentTrackingStatus
              value={shipmentStatus(row.shipmentTracking?.shipmentStatus) ?? ""}
            />
          );

        return status && <BadgeStatus value={status} />;
      },
      hideSearch: true,
      hideSort: true,
      searchIdentifier: "status_shipment",
    },
  ];

  const rowActionsExtra: RowActionsExtra[] = [
    {
      title: translate("edit"),
      key: "edit-draft",
      buttonAndAction: (_id: number, row: Waybill) => {
        if (isOnViewMode || row.shipmentTracking || row.marineTrafficId) return null;
        if (!(row as Waybill & { __localDraft?: boolean }).__localDraft) return null;

        return (
          <Tooltip title={translate("edit") ?? ""} placement="top">
            <div>
              <FiEdit2
                size={18}
                color="#308CDB"
                style={{ cursor: "pointer" }}
                onClick={() => openEditShipmentModal(row)}
              />
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: translate("remove"),
      key: "remove",
      buttonAndAction: (id: number, row: Waybill) => {
        if (isOnViewMode) return null;

        return (
          <Tooltip title={translate("remove") ?? ""} placement="top">
            <div>
              <MdOutlineCancel
                size={20}
                color="#F97066"
                style={{ cursor: "pointer" }}
                onClick={() => onDeleteWaybillFromOrder(id, row)}
              />
            </div>
          </Tooltip>
        );
      },
    },
  ];

  const renderEmptyWaybill = () => {
    if (isOnViewMode || inlineDraftCreation) return null;

    return (
      <EmptyContainer compact={compact}>
        <img src={EMPTY_DOCUMENT} alt="empty document" />
        <EmptyTitle>{translate("noShipmentAssociatedYet")}</EmptyTitle>
      </EmptyContainer>
    );
  };

  const renderInlineShipmentDraft = () => {
    if (!inlineDraftCreation || isOnViewMode) return null;

    return (
      <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1, mt: 2, overflow: "hidden" }}>
        <ShipmentProvider
          key={`inline-shipment-${providerKey}-${waybills.length}`}
          initialShipment={undefined}
          minContainerSequence={inlineMinContainerSequence}
          orderContext={order}
          syncReferenceWithOrder
        >
          <ShipmentMaintenance
            variant="orderDraft"
            onOrderDraftConfirm={handleOrderDraftConfirm}
            onOrderDraftCancel={onCancel}
          />
        </ShipmentProvider>
      </Box>
    );
  };

  return (
    <Wrapper isOnViewMode={isOnViewMode ?? false} compact={compact}>
      {!isOnViewMode && (
        <Header>
          <HeaderTitle>{translate("shipment")}</HeaderTitle>
          {!inlineDraftCreation && (
            <div style={{ display: "flex", gap: 8 }}>
              <Button customColor={Color.SECONDARY} onClick={openNewShipmentModal}>
                <FiPlusCircle size={16} />
                <ButtonLabel>{translate("createShipment")}</ButtonLabel>
              </Button>
              <Button
                customColor={Color.SECONDARY}
                onClick={() =>
                  modalAddWaybillRef.current?.openModal(
                    (waybills ?? []).filter((w: Waybill & { __localDraft?: boolean }) => !w?.__localDraft)
                  )
                }
              >
                <FiPlusCircle size={16} />
                <ButtonLabel>{translate("addShipmentToOrder")}</ButtonLabel>
              </Button>
            </div>
          )}
        </Header>
      )}
      {!waybills.length ? (
        renderEmptyWaybill()
      ) : (
        <ShipeeziNewGrid
          data={waybills ?? []}
          columns={columns}
          totalItems={waybills.length}
          positionActionsColumn="right"
          rowActionsExtra={isOnViewMode ? undefined : rowActionsExtra}
          showSelection={!isOnViewMode}
          selecteds={selectedWaybillIds}
          handleSelect={(rows: number[]) => setSelectedWaybillIds(rows)}
        />
      )}

      {renderInlineShipmentDraft()}

      <ModalAddWaybill
        ref={modalAddWaybillRef}
        waybillsFromOrder={waybills ?? []}
        columns={columns}
        containerSizes={containerSizes}
        onSave={(newWaybills) => {
          const localDrafts = (waybills ?? []).filter(
            (w: Waybill & { __localDraft?: boolean }) => !!w.__localDraft
          );
          onUpdate?.([...localDrafts, ...newWaybills]);
        }}
      />

      <Modal open={openShipmentModal} onClose={closeShipmentModal}>
        <Box sx={fullScreenModalSx}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 2,
              py: 1,
              borderBottom: 1,
              borderColor: "divider",
              flexShrink: 0,
            }}
          >
            <Box component="span" sx={{ fontWeight: 600, fontSize: 16 }}>
              {draftBeingEdited ? translate("edit") : translate("createShipment")}
            </Box>
            <IconButton aria-label="close" onClick={closeShipmentModal} size="small">
              <FiX size={20} />
            </IconButton>
          </Box>
          <Box sx={{ flex: 1, overflow: "auto", minHeight: 0 }}>
            <ShipmentProvider key={providerKey} initialShipment={initialShipmentForProvider} minContainerSequence={minContainerSequence} orderContext={order}>
              <ShipmentMaintenance
                variant="orderDraft"
                onOrderDraftConfirm={handleOrderDraftConfirm}
                onOrderDraftCancel={closeShipmentModal}
              />
            </ShipmentProvider>
          </Box>
        </Box>
      </Modal>

      <Dialog
        open={!!confirmRemoveTarget}
        onClose={() => setConfirmRemoveTarget(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{translate("removeShipmentFromOrder")}</DialogTitle>
        <DialogContent>
          <Typography>
            {translate("thisShipmentHasNoOtherOrdersAssociated")}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            customColor={Color.CANCEL}
            onClick={() => setConfirmRemoveTarget(null)}
          >
            {translate("close")}
          </Button>
          <Button
            customColor={Color.SUCCESS_OUTLINED}
            onClick={() => handleConfirmRemoveAction("revert_to_draft")}
          >
            {translate(confirmRemoveTarget?.statusShipment === "Draft" ? "leaveAsDraft" : "revertToDraft")}
          </Button>
          <Button
            customColor={Color.ERROR}
            onClick={() => handleConfirmRemoveAction("cancel")}
          >
            {translate("cancelShipment")}
          </Button>
        </DialogActions>
      </Dialog>

      {!isOnViewMode && showActionButtons && (
        <ButtonsWrapper>
          <ActionButtons
            cancelDisabled={isUpdating}
            loading={isUpdating}
            onCancel={onCancel}
            onSave={onSave}
            onSavePublish={!isUpdating ? onSaveAndPublish : undefined}
          />
        </ButtonsWrapper>
      )}
    </Wrapper>
  );
}
